// ─── Grille horaire à échelle VARIABLE (dilatation des heures denses) ──
// Porté de MoPCal (CalendrierScreen.grilleHoraire / layoutChevauchement).
// Module PUR (aucune dépendance UI) → réutilisable et testable.
//   • construireGrille : chaque heure a un « pas effectif » (plus fin si ses
//     événements sont serrés → l'heure devient plus HAUTE). minToPx = échelle
//     minute→pixel variable selon l'heure. lignes = repères d'heure + sous-lignes.
//   • layoutChevauchement : positionne les blocs en PIXELS ; deux blocs côte à
//     côte seulement s'ils se chevauchent réellement à l'écran.
import { parseHeureMin } from './affichageConfig';

const PAS_SET = [5, 10, 15, 30, 45, 60, 120];

export function construireGrille(timedEvs, opts = {}) {
  const {
    plageDebut = 7 * 60, plageFin = 22 * 60, pasDefaut = 60, row = 30, dureeDefaut = 45,
    etendre = true,        // false = plage STRICTE (les hors-plage sont rognés)
    bornesSupp = [],       // minutes supplémentaires à couvrir (bornes de plages multi-jours…)
  } = opts;
  let start = plageDebut, end = plageFin;
  const debuts = [];
  for (const ev of (timedEvs || [])) {
    const s = parseHeureMin(ev.heure); if (s == null) continue;
    const e = parseHeureMin(ev.heureFin) || s + dureeDefaut;
    debuts.push(s);
    if (etendre) {
      if (s < start) start = s;
      if (e > end) end = e;
    }
  }
  if (etendre) for (const m of bornesSupp) {
    if (m == null) continue;
    if (m < start) start = m;
    if (m > end) end = m;
  }
  start = Math.floor(start / 60) * 60;
  end = Math.max(start + 60, Math.ceil(end / 60) * 60);
  const ROW = Math.max(14, Math.round(row));
  const debutsTri = debuts.slice().sort((a, b) => a - b);

  const segments = []; let top = 0;
  // Pas UNIFORME = pas configuré (plus de dilatation automatique des heures
  // denses : elle rendait certaines heures plus hautes que les autres — les
  // événements serrés restent lisibles via le positionnement proportionnel
  // et le rendu côte à côte).
  const eff = PAS_SET.filter(p => p <= pasDefaut).pop() || PAS_SET[0];
  for (let h = start / 60; h < end / 60; h++) {
    const hStart = h * 60;
    const height = (60 / eff) * ROW;
    segments.push({ hStart, eff, top, height });
    top += height;
  }
  const hauteur = top || ROW;

  const minToPx = (min) => {
    if (min <= start) return 0;
    if (min >= end) return hauteur;
    const seg = segments[Math.floor((min - start) / 60)];
    return seg ? seg.top + (min - seg.hStart) / 60 * seg.height : 0;
  };

  const lignes = [];
  for (const seg of segments) {
    const nb = Math.max(1, Math.round(60 / seg.eff));
    for (let i = 0; i < nb; i++) {
      const m = seg.hStart + i * seg.eff;
      if (m >= end) break;
      lignes.push({ top: minToPx(m), heure: i === 0, label: i === 0 ? `${Math.floor(m / 60)}h` : (seg.eff < 60 ? `:${String(m % 60).padStart(2, '0')}` : null) });
    }
  }
  const effAt = (min) => { const i = Math.floor((Math.max(start, Math.min(end - 1, min)) - start) / 60); return segments[i] ? segments[i].eff : pasDefaut; };
  // Inverse de minToPx : position verticale (px) → minutes (pour créer via appui long).
  const pxToMin = (px) => {
    if (px <= 0) return start;
    if (px >= hauteur) return end;
    const seg = segments.find(s => px < s.top + s.height) || segments[segments.length - 1];
    return Math.round(seg.hStart + ((px - seg.top) / seg.height) * 60);
  };
  return { start, end, hauteur, minToPx, pxToMin, effAt, ROW, segments, lignes, plageDebut, plageFin };
}

// Répartit en COLONNES des items déjà positionnés en pixels ({ topPx, botPx, … }).
// Deux items côte à côte seulement s'ils se chevauchent réellement à l'écran
// (grappes) — même rendu qu'Outlook. Chaque item ressort avec { col, nCols }.
export function layoutItemsPositionnes(items) {
  const tri = (items || []).slice().sort((a, b) => a.topPx - b.topPx || a.botPx - b.botPx);
  const res = []; let grappe = []; let finGrappe = -Infinity; const EPS = 1;
  const vider = () => {
    const cols = [];
    for (const it of grappe) {
      let k = cols.findIndex(bot => it.topPx >= bot - EPS);
      if (k === -1) { k = cols.length; cols.push(it.botPx); } else { cols[k] = it.botPx; }
      it.col = k;
    }
    for (const it of grappe) res.push({ ...it, nCols: cols.length });
    grappe = []; finGrappe = -Infinity;
  };
  for (const it of tri) {
    if (grappe.length && it.topPx >= finGrappe - EPS) vider();
    grappe.push(it); finGrappe = Math.max(finGrappe, it.botPx);
  }
  if (grappe.length) vider();
  return res;
}

export function layoutChevauchement(evs, G, dureeDefaut = 45) {
  const ROW = G ? G.ROW : 24;
  const items = (evs || []).map(ev => {
    const s = parseHeureMin(ev.heure);
    if (s == null) return null;
    const fin = ev.heureFin ? parseHeureMin(ev.heureFin) : null;
    const topPx = G ? G.minToPx(s) : 0;
    const hPx = fin != null ? Math.max((G ? G.minToPx(fin) : 0) - topPx, 14) : ROW;
    return { ev, s, topPx, botPx: topPx + hPx, hPx };
  }).filter(Boolean).sort((a, b) => a.topPx - b.topPx || a.botPx - b.botPx);

  // Événement « heure de début seule » : plafonne sa hauteur à l'écart au suivant.
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it.ev.heureFin) continue;
    const next = items[i + 1];
    if (next) { const cap = Math.max(14, Math.min(it.hPx, next.topPx - it.topPx)); it.hPx = cap; it.botPx = it.topPx + cap; }
  }

  return layoutItemsPositionnes(items).map(({ ev, topPx, hPx, col, nCols }) => ({ ev, topPx, hPx, col, nCols }));
}
